<template>
  <div class="form-wrapper">
    <div class="form-fields-wrapper">
      <base-form-field
        v-model="personalInfo.firstName"
        :schema="schema.firstName"
        autocomplete="given-name"
      />
      <base-form-field
        class="noWrapLabelText"
        v-model="personalInfo.middleName"
        :schema="schema.middleName"
        autocomplete="additional-name"
      />
      <base-form-field
        v-model="personalInfo.lastName"
        :schema="schema.lastName"
        autocomplete="family-name"
      />
    </div>
    <div class="form-buttons-wrapper"><next-button @click="submit" text="Submit" /></div>
  </div>
</template>
<script setup lang="ts">
import { $api } from "@/services/api";
import { reactive, watch, ref } from "vue";
import type { FieldSchema, FormSchema } from "@/models/form";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import { useStore } from "vuex";
import NextButton from "@/components/shared/NextButton.vue";
import useValidation from "@/use/validation";
import type { Applicant, Address } from "@/models/application";
import type { Stipulation } from "@/models/opal";
import type { NameInputRequest, StipulationInputRequest } from "@/models/api";
import _ from "lodash";

useStore();

interface Props {
  address?: Address;
  applicant: Applicant;
  stip: Stipulation;
}

const props = withDefaults(defineProps<Props>(), {});

const emit = defineEmits<{
  (e: "success", updatedStip: Stipulation): void;
}>();

const { validateForm } = useValidation();

let personalInfo = ref({} as any);

const submittingData = ref<boolean>(false);

const schema = reactive<FormSchema>({
  firstName: {
    name: "firstName",
    type: "text",
    label: "First Name",
    required: true,
    errorMessage: "First Name is required.",
    maxLength: 40,
  } as FieldSchema,
  middleName: {
    name: "middleName",
    type: "text",
    label: "Middle Name",
    required: false,
    maxLength: 40,
  } as FieldSchema,
  lastName: {
    name: "lastName",
    type: "text",
    label: "Last Name",
    required: true,
    errorMessage: "Last Name is required.",
    maxLength: 40,
  } as FieldSchema,
} as FormSchema);

function handleMiddleNameFieldValidation(value: string) {
  if (value && value.length > 0) {
    schema.middleName.required = true;
  } else {
    personalInfo.value.middleName = undefined;
    schema.middleName.required = false;
  }
}

async function submit() {
  submittingData.value = true;
  const valid = validateForm(schema, personalInfo.value);
  if (valid) {
    const request = {
      applicationStipulationId: props.stip?.id,
      applicationId: props?.stip?.applicationId,
      nameInput: {
        firstName: personalInfo.value.firstName,
        middleName: personalInfo.value.middleName,
        lastName: personalInfo.value.lastName,
      } as NameInputRequest,
    } as StipulationInputRequest;

    const response = await $api.applications.addStipInput(props?.stip?.applicationId, request);

    if (response) {
      // updates stip status to pending
      let updatedNameVerificationStip = props.stip;
      updatedNameVerificationStip.stipulationStatusId = response.stipulationStatusId;
      updatedNameVerificationStip.stipulationStatusName = response.stipulationStatusName;

      emit("success", updatedNameVerificationStip);

      submittingData.value = false;
    } else {
      submittingData.value = false;
    }
  } else {
    submittingData.value = false;
  }
}

watch(
  () => _.cloneDeep(personalInfo.value),
  (currentValue, oldValue) => {
    if (currentValue.middleName || currentValue.middleName === "") {
      handleMiddleNameFieldValidation(currentValue.middleName);
    }
  },
);
</script>
<style scoped>
.form-wrapper .form-fields-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1em;
}
.form-wrapper .form-buttons-wrapper {
  margin-top: 1rem;
}
</style>
